import React, { useState } from "react";
import axios from "axios";
import { 
  Heart, Calendar, FileText, Users, Mail, Phone, MapPin, 
  Menu, X, Search, ChevronRight, ExternalLink, Download, 
  Send, ShieldCheck, Sparkles, Globe, Award, BookOpen, 
  CheckCircle2, MessageSquare, DollarSign, Handshake,
  Scale, Building2, HeartHandshake, AlertTriangle, PhoneCall,
  Target, Compass
} from "lucide-react";
import { 
  siteInfo, leaders, events, documents, newsArticles, testimonials,
  coreValues, strategicObjectives, workAreas, programs, trataInfo,
  communityActions, alliances, socialImpact, contactReasons
} from "./mock";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  ShieldCheck, Scale, Building2, Handshake, MapPin,
  Users, BookOpen, HeartHandshake
};

export default function App() {
  const [activeTab, setActiveTab] = useState("inicio");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", reason: "Solicitar asistencia", message: "" });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  const [registrationEvent, setRegistrationEvent] = useState(null);
  const [regForm, setRegForm] = useState({ name: "", email: "" });
  const [regSubmitting, setRegSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Por favor completa los campos obligatorios.");
      return;
    }
    setContactSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSubmitted(true);
      toast.success("Mensaje enviado con éxito. Nos pondremos en contacto a la brevedad.");
      setContactForm({ name: "", email: "", phone: "", reason: "Solicitar asistencia", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No pudimos enviar tu mensaje. Intentá nuevamente en unos minutos o llamá al " + siteInfo.phone);
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setNewsletterSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("¡Te suscribiste al boletín exitosamente!");
      setNewsletterEmail("");
    } catch (err) {
      toast.error("No pudimos registrar tu suscripción. Verificá el correo e intentá nuevamente.");
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const handleEventRegister = (event) => {
    setRegForm({ name: "", email: "" });
    setRegistrationEvent(event);
  };

  const submitEventRegistration = async (e) => {
    e.preventDefault();
    if (!regForm.name || !regForm.email) {
      toast.error("Por favor completá nombre y correo.");
      return;
    }
    setRegSubmitting(true);
    try {
      await axios.post(`${API}/event-register`, {
        event_id: registrationEvent.id,
        event_title: registrationEvent.title,
        name: regForm.name,
        email: regForm.email,
      });
      toast.success(`¡Inscripción registrada para "${registrationEvent.title}"! Te esperamos.`);
      setRegistrationEvent(null);
      setRegForm({ name: "", email: "" });
    } catch (err) {
      toast.error("No pudimos registrar tu inscripción. Verificá tus datos e intentá nuevamente.");
    } finally {
      setRegSubmitting(false);
    }
  };

  const navItems = [
    { id: "inicio", label: "Inicio" },
    { id: "quienes-somos", label: "Quiénes Somos" },
    { id: "programas", label: "Programas" },
    { id: "actividades", label: "Actividades" },
    { id: "documentos", label: "Documentos" },
    { id: "contacto", label: "Contacto" },
  ];

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#2C2A29] font-sans selection:bg-[#D4A373]/30">
      <Toaster position="top-right" richColors />

      {/* Top Bar */}
      <div className="bg-[#3A4F41] text-[#F4F1EA] text-xs py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#D4A373]" />
            <span className="font-medium">Red Interreligiosa Zárate • Unidad · Dignidad · Justicia · Acción</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="hidden md:flex items-center gap-1">
              <Phone className="w-3 h-3 text-[#D4A373]" /> {siteInfo.phone}
            </span>
            <a href="tel:145" className="hidden md:flex items-center gap-1 bg-[#B14A3F] hover:bg-[#9A3E36] px-2 py-0.5 rounded font-bold text-white" data-testid="top-linea-145">
              <PhoneCall className="w-3 h-3" /> Línea 145 · Trata
            </a>
            <a
              href={siteInfo.mercadoPagoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#D4A373] hover:bg-[#C29262] text-[#2C2A29] font-semibold px-3 py-0.5 rounded transition shadow-sm"
              data-testid="top-donate-btn"
            >
              Donar / Colaborar
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="sticky top-0 z-40 bg-[#FBF9F5]/95 backdrop-blur-md border-b border-[#E8E2D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setActiveTab("inicio")} data-testid="nav-logo">
              <img 
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_comunidad-dialogos/artifacts/wg5ay1rq_Logo_Red_Interreligiosaa_oficial-removebg-preview.png" 
                alt="Red Interreligiosa Zárate" 
                className="w-14 h-14 object-contain drop-shadow"
              />
              <div>
                <h1 className="font-serif font-bold text-xs sm:text-sm text-[#2C2A29] tracking-tight leading-tight max-w-[240px]">
                  Red Interreligiosa de Protección Integral y Restitución de Derechos
                </h1>
                <p className="text-[10px] uppercase tracking-widest text-[#3A4F41] font-extrabold mt-0.5">Zárate, Buenos Aires</p>
              </div>
            </div>

            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  data-testid={`nav-${item.id}`}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeTab === item.id
                      ? "bg-[#3A4F41] text-[#F4F1EA] shadow-sm"
                      : "text-[#4A4745] hover:bg-[#EFECE6] hover:text-[#2C2A29]"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <a
                href={siteInfo.mercadoPagoLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-donate-btn"
                className="flex items-center gap-2 bg-[#D4A373] hover:bg-[#C29262] text-[#2C2A29] px-4 py-2.5 rounded-lg text-sm font-bold transition shadow-sm"
              >
                <Heart className="w-4 h-4" />
                <span>Donar / Colaborar</span>
              </a>
            </div>

            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
                className="p-2 rounded-md text-[#2C2A29] hover:bg-[#EFECE6]"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#FBF9F5] border-b border-[#E8E2D5] px-4 pt-2 pb-6 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
                className={`w-full text-left px-4 py-2.5 rounded-lg text-base font-medium ${
                  activeTab === item.id ? "bg-[#3A4F41] text-white" : "text-[#4A4745] hover:bg-[#EFECE6]"
                }`}
              >
                {item.label}
              </button>
            ))}
            <a
              href="tel:145"
              className="w-full mt-2 flex items-center justify-center gap-2 bg-[#B14A3F] text-white font-bold px-4 py-3 rounded-lg shadow"
            >
              <PhoneCall className="w-5 h-5" /> Línea 145 · Trata de Personas
            </a>
            <a
              href={siteInfo.mercadoPagoLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full flex items-center justify-center gap-2 bg-[#D4A373] text-[#2C2A29] font-bold px-4 py-3 rounded-lg shadow"
              data-testid="mobile-donate-btn"
            >
              <Heart className="w-5 h-5" />
              <span>Donar / Colaborar (Mercado Pago)</span>
            </a>
          </div>
        )}
      </header>

      <main>
        {/* ============ INICIO ============ */}
        {activeTab === "inicio" && (
          <div data-testid="view-inicio">
            <section className="relative bg-[#1E2B22] text-white overflow-hidden py-20 lg:py-28">
              <div className="absolute inset-0 opacity-20 mix-blend-overlay bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1449034446853-66c86144b0ad?auto=format&fit=crop&q=80&w=1600')` }}></div>
              <div className="absolute inset-0 bg-gradient-to-b from-[#1E2B22]/95 via-[#2D3E33]/85 to-[#1E2B22]/95"></div>
              
              <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4A373]/15 border border-[#D4A373]/40 text-[#D4A373] text-[11px] font-bold tracking-widest uppercase">
                  <ShieldCheck className="w-3.5 h-3.5" /> Institución Interreligiosa · Fundada en {siteInfo.foundedYear}
                </div>

                <div className="flex flex-col items-center gap-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#F4F1EA] rounded-full blur-xl opacity-40 scale-110"></div>
                    <div className="relative w-40 sm:w-48 h-40 sm:h-48 rounded-full bg-[#F4F1EA] flex items-center justify-center shadow-2xl border-4 border-[#D4A373]/60 p-4">
                      <img 
                        src="https://customer-assets-eiarnc6j.emergentagent.net/job_comunidad-dialogos/artifacts/wg5ay1rq_Logo_Red_Interreligiosaa_oficial-removebg-preview.png" 
                        alt="Escudo de la Red Interreligiosa Zárate" 
                        className="w-full h-full object-contain drop-shadow-lg"
                        data-testid="hero-logo"
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-white max-w-4xl mx-auto drop-shadow-lg" data-testid="hero-title">
                      Red Interreligiosa de Protección Integral y Restitución de Derechos
                    </h1>
                    <p className="text-sm sm:text-base text-[#D4A373] font-bold uppercase tracking-[0.25em] drop-shadow-md">
                      Zárate · Provincia de Buenos Aires · Argentina
                    </p>
                  </div>
                </div>

                <div className="w-24 h-px bg-[#D4A373]/70 mx-auto"></div>

                <p className="font-serif text-xl sm:text-2xl lg:text-3xl text-white font-semibold italic drop-shadow-lg">
                  {siteInfo.slogan}
                </p>
                <p className="text-sm sm:text-base text-[#F4F1EA] font-light leading-relaxed max-w-2xl mx-auto drop-shadow">
                  {siteInfo.subtitle}
                </p>

                <div className="flex flex-wrap gap-4 pt-4 justify-center">
                  <button
                    onClick={() => setActiveTab("contacto")}
                    className="bg-[#D4A373] hover:bg-[#C29262] text-[#2C2A29] font-bold px-7 py-3.5 rounded-xl transition shadow-lg flex items-center gap-2"
                    data-testid="hero-asistencia-btn"
                  >
                    <span>Solicitar Asistencia</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setActiveTab("quienes-somos")}
                    className="bg-white/10 hover:bg-white/20 text-white border border-white/40 font-semibold px-7 py-3.5 rounded-xl transition backdrop-blur-sm"
                    data-testid="hero-about-btn"
                  >
                    Conocer la Red
                  </button>
                </div>
              </div>
            </section>

            {/* Franja de articulación institucional */}
            <section className="bg-[#F4F1EA] border-y border-[#E4E0D6] py-6" data-testid="articulacion-strip">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-[#5C7065] mb-4">
                  Articulados con Redes Locales · Provinciales · Nacionales e Internacionales
                </p>
                <div className="flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-10 gap-y-3 text-[11px] sm:text-xs font-semibold text-[#3A4F41] uppercase tracking-wide">
                  <span>Municipalidad de Zárate</span>
                  <span className="text-[#D4A373]">·</span>
                  <span>Provincia de Buenos Aires</span>
                  <span className="text-[#D4A373]">·</span>
                  <span>Comité Ejecutivo Nacional contra la Trata</span>
                  <span className="text-[#D4A373]">·</span>
                  <span>Cáritas Argentina</span>
                  <span className="text-[#D4A373]">·</span>
                  <span>Redes Interreligiosas Internacionales</span>
                </div>
              </div>
            </section>

            {/* Línea 145 destacada */}
            <section className="relative bg-[#1E2B22] text-white py-12 overflow-hidden" data-testid="linea-145-section">
              <div 
                className="absolute inset-0 opacity-40 bg-cover bg-center" 
                style={{ backgroundImage: `url('https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&q=80&w=1600')` }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#1E2B22]/95 via-[#2D3E33]/70 to-[#1E2B22]/95"></div>
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                  <div className="w-20 h-20 rounded-2xl bg-[#B14A3F] flex flex-col items-center justify-center shrink-0 shadow-2xl border-2 border-white/20">
                    <span className="font-serif text-3xl font-bold text-white leading-none">145</span>
                    <span className="text-[9px] font-bold text-[#F7D1CB] uppercase tracking-wider mt-0.5">Trata</span>
                  </div>
                  <div className="drop-shadow-md">
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[#D4A373]">Línea Nacional contra la Trata de Personas</p>
                    <p className="font-serif text-xl sm:text-2xl lg:text-3xl font-bold text-white mt-1">Marcá el 145 · Gratuita · 24 hs · Confidencial</p>
                    <p className="text-xs sm:text-sm text-[#C8C0B0] mt-1">Denunciá, pedí ayuda o solicitá información sobre trata y explotación.</p>
                  </div>
                </div>
                <a
                  href="tel:145"
                  data-testid="hero-linea-145-btn"
                  className="bg-[#B14A3F] hover:bg-[#8A3A32] text-white font-bold px-6 py-3.5 rounded-xl shadow-lg flex items-center gap-2 transition border border-white/20"
                >
                  <PhoneCall className="w-5 h-5" />
                  <span>Llamar al 145</span>
                </a>
              </div>
            </section>

            {/* Stats */}
            <section className="bg-[#3A4F41] text-white py-10">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div className="p-4 border-r border-white/10 last:border-none">
                    <div className="font-serif text-3xl sm:text-4xl font-bold text-[#D4A373]">6</div>
                    <div className="text-sm text-[#E8E2D5] mt-1">Áreas de Trabajo</div>
                  </div>
                  <div className="p-4 border-r border-white/10 last:border-none">
                    <div className="font-serif text-3xl sm:text-4xl font-bold text-[#D4A373]">5</div>
                    <div className="text-sm text-[#E8E2D5] mt-1">Programas Institucionales</div>
                  </div>
                  <div className="p-4 border-r border-white/10 last:border-none">
                    <div className="font-serif text-3xl sm:text-4xl font-bold text-[#D4A373]">45+</div>
                    <div className="text-sm text-[#E8E2D5] mt-1">Encuentros Realizados</div>
                  </div>
                  <div className="p-4">
                    <div className="font-serif text-3xl sm:text-4xl font-bold text-[#D4A373]">1500+</div>
                    <div className="text-sm text-[#E8E2D5] mt-1">Familias Asistidas</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Identidad + imagen */}
            <section className="py-20 bg-[#3A4F41] text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-6">
                    <div className="inline-block text-[#D4A373] text-xs font-bold uppercase tracking-widest bg-white/10 px-3 py-1 rounded border border-[#D4A373]/40">
                      Identidad Institucional
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white">
                      Un espacio de articulación por la dignidad humana
                    </h2>
                    <p className="text-[#E8E2D5] text-base leading-relaxed">
                      {siteInfo.identity}
                    </p>
                    <blockquote className="border-l-4 border-[#D4A373] pl-4 italic text-[#D4A373] font-serif text-lg">
                      "{siteInfo.lema}"
                    </blockquote>
                    <div>
                      <button
                        onClick={() => setActiveTab("quienes-somos")}
                        className="inline-flex items-center gap-2 text-[#D4A373] font-bold hover:text-white transition"
                      >
                        <span>Conocer nuestra misión, visión y valores</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_comunidad-dialogos/artifacts/qpvbefqa_Red%20imagen%201%20reunion.png" alt="Reunión interreligiosa de líderes de la Red en Zárate" className="rounded-2xl shadow-md object-cover h-64 w-full" />
                    <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_comunidad-dialogos/artifacts/7vjlbm3m_entrega%20alimentos%20y%20ropa.png" alt="Voluntarios de la Red entregando alimentos y ropa en un barrio de Zárate" className="rounded-2xl shadow-md object-cover h-64 w-full mt-8" />
                  </div>
                </div>
              </div>
            </section>

            {/* Objetivos Estratégicos */}
            <section className="bg-[#2D3E33] py-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
                <div className="text-center max-w-2xl mx-auto space-y-3">
                  <span className="text-[#D4A373] text-xs font-bold uppercase tracking-widest bg-white/10 px-3 py-1 rounded border border-[#D4A373]/40">Ejes Estratégicos</span>
                  <h2 className="font-serif text-3xl font-bold text-white">Objetivos Estratégicos</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                  {strategicObjectives.map((obj) => {
                    const Icon = iconMap[obj.icon] || Target;
                    return (
                      <div key={obj.id} className="bg-white/5 backdrop-blur-sm p-6 rounded-2xl border border-white/10 space-y-3 hover:bg-white/10 transition" data-testid={`objective-${obj.id}`}>
                        <div className="w-12 h-12 rounded-xl bg-[#D4A373]/20 text-[#D4A373] flex items-center justify-center">
                          <Icon className="w-6 h-6" />
                        </div>
                        <h3 className="font-serif text-lg font-bold text-white">{obj.title}</h3>
                        <p className="text-sm text-[#E8E2D5] leading-relaxed">{obj.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Eventos destacados */}
            <section className="bg-[#3A4F41] py-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
                  <div>
                    <span className="text-[#D4A373] text-xs font-bold uppercase tracking-widest bg-white/10 px-3 py-1 rounded border border-[#D4A373]/40">Agenda</span>
                    <h2 className="font-serif text-3xl font-bold text-white mt-2">Próximos Encuentros y Actividades</h2>
                  </div>
                  <button onClick={() => setActiveTab("actividades")} className="text-[#D4A373] font-semibold text-sm hover:underline flex items-center gap-1">
                    <span>Ver todos</span><ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {events.slice(0, 3).map((event) => (
                    <div key={event.id} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-[#E4E0D6] flex flex-col group hover:shadow-xl transition">
                      <div className="relative h-48 overflow-hidden">
                        <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                        <span className="absolute top-4 left-4 bg-[#3A4F41] text-white text-xs font-semibold px-3 py-1 rounded-full">{event.category}</span>
                      </div>
                      <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs text-[#5C7065] font-medium">
                            <Calendar className="w-4 h-4 text-[#D4A373]" />
                            <span>{event.date} • {event.time}</span>
                          </div>
                          <h3 className="font-serif text-xl font-bold text-[#2C2A29]">{event.title}</h3>
                          <p className="text-sm text-[#5C7065] line-clamp-2">{event.description}</p>
                        </div>
                        <div className="pt-4 border-t border-[#F2ECE1] flex items-center justify-between text-xs text-[#5C7065]">
                          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#D4A373]" /> {event.location}</span>
                          <button onClick={() => setActiveTab("actividades")} className="font-bold text-[#3A4F41] hover:underline">Detalles →</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Noticias */}
            <section className="bg-[#2D3E33] py-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
                  <span className="text-[#D4A373] text-xs font-bold uppercase tracking-widest bg-white/10 px-3 py-1 rounded border border-[#D4A373]/40">Actualidad</span>
                  <h2 className="font-serif text-3xl font-bold text-white">Noticias y Comunicados Recientes</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {newsArticles.map((article) => (
                    <div key={article.id} className="bg-white rounded-xl border border-[#E8E2D5] overflow-hidden shadow-lg hover:shadow-xl transition flex flex-col">
                      <img src={article.image} alt={article.title} className="w-full h-48 object-cover" />
                      <div className="p-6 flex-1 flex flex-col justify-between space-y-3">
                        <div>
                          <div className="text-xs font-semibold text-[#D4A373] uppercase tracking-wide mb-1">{article.category} • {article.date}</div>
                          <h3 className="font-serif font-bold text-lg text-[#2C2A29]">{article.title}</h3>
                          <p className="text-sm text-[#5C7065] mt-2">{article.excerpt}</p>
                        </div>
                        <button onClick={() => setActiveTab("documentos")} className="text-xs font-bold text-[#3A4F41] hover:underline pt-2 flex items-center gap-1">
                          <span>Leer más</span><ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Newsletter */}
            <section className="bg-[#3A4F41] text-white py-16">
              <div className="max-w-4xl mx-auto px-4 text-center space-y-6">
                <div className="inline-flex p-3 rounded-full bg-[#D4A373]/20 text-[#D4A373]">
                  <Mail className="w-6 h-6" />
                </div>
                <h2 className="font-serif text-3xl font-bold">Suscríbete al Boletín de la Red</h2>
                <p className="text-[#E8E2D5] text-sm max-w-lg mx-auto">
                  Recibe en tu correo notificaciones sobre nuestros encuentros, comunicados oficiales y jornadas solidarias.
                </p>
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto pt-2">
                  <input type="email" required placeholder="Tu correo electrónico..." value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="flex-1 px-4 py-3 rounded-xl text-[#2C2A29] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373] text-sm" data-testid="newsletter-input" />
                  <button type="submit" disabled={newsletterSubmitting} className="bg-[#D4A373] hover:bg-[#C29262] disabled:opacity-60 disabled:cursor-not-allowed text-[#2C2A29] font-bold px-6 py-3 rounded-xl transition shadow" data-testid="newsletter-submit">
                    {newsletterSubmitting ? "Enviando..." : "Suscribirme"}
                  </button>
                </form>
              </div>
            </section>
          </div>
        )}

        {/* ============ QUIENES SOMOS ============ */}
        {activeTab === "quienes-somos" && (
          <div data-testid="view-quienes-somos" className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
            <div className="text-center max-w-3xl mx-auto space-y-4">
              <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">Institucional</span>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C2A29]">Quiénes Somos</h1>
              <p className="text-[#5C7065] text-lg leading-relaxed">{siteInfo.identity}</p>
            </div>

            {/* Misión/Visión/Lema */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-8 rounded-2xl border border-[#E8E2D5] shadow-xs space-y-4">
                <div className="w-12 h-12 rounded-xl bg-[#3A4F41]/10 text-[#3A4F41] flex items-center justify-center">
                  <Target className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#2C2A29]">Misión</h3>
                <p className="text-sm text-[#5C7065] leading-relaxed">{siteInfo.mission}</p>
              </div>
              <div className="bg-white p-8 rounded-2xl border border-[#E8E2D5] shadow-xs space-y-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4A373]/20 text-[#D4A373] flex items-center justify-center">
                  <Compass className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#2C2A29]">Visión</h3>
                <p className="text-sm text-[#5C7065] leading-relaxed">{siteInfo.vision}</p>
              </div>
              <div className="bg-[#3A4F41] text-white p-8 rounded-2xl shadow-md space-y-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 text-[#D4A373] flex items-center justify-center">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-xl font-bold text-white">Lema</h3>
                <p className="text-sm text-[#E8E2D5] leading-relaxed italic">"{siteInfo.lema}" Desde la cooperación, el diálogo y la acción concreta, la Red trabaja para acompañar a quienes más lo necesitan.</p>
              </div>
            </div>

            {/* Valores */}
            <div className="space-y-8">
              <div className="text-center max-w-2xl mx-auto space-y-3">
                <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">Principios</span>
                <h2 className="font-serif text-3xl font-bold text-[#2C2A29]">Valores Fundamentales</h2>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                {coreValues.map((v, i) => (
                  <span key={i} data-testid={`value-${i}`} className="bg-white border border-[#E4E0D6] px-4 py-2 rounded-full text-sm text-[#3A4F41] font-medium shadow-xs hover:bg-[#EFECE6] transition">
                    {v}
                  </span>
                ))}
              </div>
            </div>

            {/* Áreas de Trabajo */}
            <div className="space-y-10">
              <div className="text-center max-w-2xl mx-auto space-y-3">
                <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">Estructura Operativa</span>
                <h2 className="font-serif text-3xl font-bold text-[#2C2A29]">Áreas de Trabajo</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {workAreas.map((area) => {
                  const Icon = iconMap[area.icon] || Users;
                  return (
                    <div key={area.id} className="bg-white p-6 rounded-2xl border border-[#E8E2D5] shadow-xs space-y-4 hover:shadow-md transition" data-testid={`area-${area.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#D4A373]/20 text-[#D4A373] flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                        <h3 className="font-serif text-lg font-bold text-[#2C2A29]">{area.title}</h3>
                      </div>
                      <ul className="space-y-2">
                        {area.items.map((it, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#5C7065]">
                            <CheckCircle2 className="w-4 h-4 text-[#3A4F41] shrink-0 mt-0.5" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Líderes */}
            <div className="space-y-12">
              <div className="text-center max-w-2xl mx-auto space-y-3">
                <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">Liderazgo</span>
                <h2 className="font-serif text-3xl font-bold text-[#2C2A29]">Líderes y Referentes de la Red</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {leaders.map((leader) => (
                  <div key={leader.id} className="bg-white rounded-2xl overflow-hidden border border-[#E8E2D5] shadow-xs flex flex-col text-center p-6 space-y-4 hover:shadow-md transition">
                    <div className="w-28 h-28 mx-auto rounded-full overflow-hidden border-4 border-[#D4A373]/30 shadow-inner">
                      <img src={leader.image} alt={leader.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs font-semibold uppercase tracking-wider text-[#3A4F41] bg-[#3A4F41]/10 px-2.5 py-1 rounded-full">
                        {leader.tradition}
                      </span>
                      <h3 className="font-serif text-xl font-bold text-[#2C2A29] mt-2">{leader.name}</h3>
                      <p className="text-xs font-semibold text-[#D4A373]">{leader.role}</p>
                    </div>
                    <p className="text-sm text-[#5C7065] leading-relaxed pt-2 border-t border-[#F4F1EA]">{leader.bio}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Impacto Social */}
            <div className="bg-[#3A4F41] text-white rounded-3xl p-8 sm:p-12 space-y-8">
              <div className="text-center max-w-xl mx-auto space-y-3">
                <span className="text-[#D4A373] text-xs font-bold uppercase tracking-widest">Nuestra Proyección</span>
                <h2 className="font-serif text-3xl font-bold">Impacto Social Esperado</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {socialImpact.map((s, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 p-5 rounded-xl space-y-2" data-testid={`impact-${i}`}>
                    <div className="font-serif text-2xl font-bold text-[#D4A373]">{String(i + 1).padStart(2, "0")}</div>
                    <p className="text-sm text-[#E8E2D5] leading-relaxed">{s}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Alianzas */}
            <div className="space-y-8">
              <div className="text-center max-w-2xl mx-auto space-y-3">
                <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">Red de Alianzas</span>
                <h2 className="font-serif text-3xl font-bold text-[#2C2A29]">Vinculación Institucional</h2>
                <p className="text-[#5C7065] text-sm">La Red promueve vínculos con instituciones locales, provinciales, nacionales e internacionales.</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {alliances.map((a, i) => (
                  <div key={i} className="bg-white border border-[#E8E2D5] rounded-xl p-4 text-center text-sm font-medium text-[#3A4F41] hover:bg-[#EFECE6] transition" data-testid={`alliance-${i}`}>
                    {a}
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonios */}
            <div className="bg-[#EFECE6] rounded-3xl p-8 sm:p-12 space-y-8">
              <div className="text-center max-w-xl mx-auto">
                <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C2A29]">Lo que dicen nuestros vecinos</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-white p-6 rounded-2xl shadow-xs border border-[#E4E0D6] space-y-4">
                    <p className="text-sm text-[#4A4745] italic leading-relaxed">"{t.quote}"</p>
                    <div>
                      <h4 className="font-serif font-bold text-sm text-[#2C2A29]">{t.author}</h4>
                      <p className="text-xs text-[#5C7065]">{t.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ============ PROGRAMAS ============ */}
        {activeTab === "programas" && (
          <div data-testid="view-programas" className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
            <div className="text-center max-w-3xl mx-auto space-y-4">
              <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">Nuestros Programas</span>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C2A29]">Programas Institucionales</h1>
              <p className="text-[#5C7065] text-lg">Cinco líneas de trabajo articuladas que sostienen la acción de la Red en el territorio de Zárate.</p>
            </div>

            {/* Trata de Personas destacada */}
            <div className="relative bg-gradient-to-br from-[#B14A3F] to-[#8A3A32] text-white rounded-3xl p-8 sm:p-12 shadow-lg space-y-8 overflow-hidden" data-testid="trata-section">
              <div 
                className="absolute inset-0 opacity-35 bg-cover bg-center rounded-3xl mix-blend-overlay" 
                style={{ backgroundImage: `url('https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&q=80&w=1600')` }}
              ></div>
              <div className="relative">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-2 space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[#F7D1CB] text-xs font-bold uppercase tracking-wider">
                    <AlertTriangle className="w-4 h-4" /> Área Especializada
                  </div>
                  <h2 className="font-serif text-3xl sm:text-4xl font-bold">{trataInfo.title}</h2>
                  <p className="text-[#F7D1CB] leading-relaxed">{trataInfo.intro}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                    <div>
                      <h3 className="font-serif text-lg font-bold text-white mb-3">Ámbitos de Intervención</h3>
                      <ul className="space-y-2">
                        {trataInfo.intervention.map((it, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#F7D1CB]">
                            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-white" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-serif text-lg font-bold text-white mb-3">Acciones Proyectadas</h3>
                      <ul className="space-y-2">
                        {trataInfo.actions.map((it, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#F7D1CB]">
                            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-white" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="bg-white text-[#2C2A29] rounded-2xl p-6 shadow-md space-y-4 text-center">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#B14A3F]">Línea de ayuda</div>
                  <div className="font-serif text-6xl font-bold text-[#B14A3F]">{trataInfo.helpLine.number}</div>
                  <p className="text-sm text-[#5C7065] leading-relaxed">{trataInfo.helpLine.label}</p>
                  <p className="text-xs text-[#5C7065] italic">{trataInfo.helpLine.detail}</p>
                  <a href="tel:145" data-testid="programas-linea-145" className="w-full bg-[#B14A3F] hover:bg-[#8A3A32] text-white font-bold py-3 rounded-xl transition flex items-center justify-center gap-2">
                    <PhoneCall className="w-4 h-4" /> Llamar al 145
                  </a>
                </div>
              </div>
              </div>
            </div>

            {/* Programas Institucionales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {programs.map((p) => (
                <div key={p.id} className="bg-white p-8 rounded-2xl border border-[#E8E2D5] shadow-xs space-y-4 hover:shadow-md transition" data-testid={`program-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#3A4F41] text-[#D4A373] flex items-center justify-center font-serif font-bold text-lg">
                      {p.id}
                    </div>
                    <h3 className="font-serif text-xl font-bold text-[#2C2A29]">{p.title}</h3>
                  </div>
                  <p className="text-sm text-[#5C7065] leading-relaxed">{p.description}</p>
                  <ul className="space-y-2 pt-2 border-t border-[#F4F1EA]">
                    {p.actions.map((a, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#3A3836]">
                        <ChevronRight className="w-4 h-4 text-[#D4A373] shrink-0 mt-0.5" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Acción Comunitaria */}
            <div className="space-y-10">
              <div className="text-center max-w-2xl mx-auto space-y-3">
                <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">En el Territorio</span>
                <h2 className="font-serif text-3xl font-bold text-[#2C2A29]">Acción Comunitaria</h2>
                <p className="text-[#5C7065] text-sm">Presencia territorial en cada barrio de Zárate.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {communityActions.map((ca, i) => (
                  <div key={i} className="bg-[#EFECE6] p-6 rounded-2xl border border-[#E4E0D6] space-y-4" data-testid={`community-${i}`}>
                    <h3 className="font-serif text-lg font-bold text-[#3A4F41]">{ca.title}</h3>
                    <ul className="space-y-2">
                      {ca.items.map((it, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-[#4A4745]">
                          <CheckCircle2 className="w-4 h-4 text-[#3A4F41] shrink-0 mt-0.5" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ============ ACTIVIDADES ============ */}
        {activeTab === "actividades" && (
          <div data-testid="view-actividades" className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
            <div className="text-center max-w-3xl mx-auto space-y-4">
              <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">Agenda Abierta</span>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C2A29]">Actividades y Eventos</h1>
              <p className="text-[#5C7065] text-lg">Participe en nuestros encuentros de oración, foros, capacitaciones y jornadas solidarias abiertas a toda la comunidad.</p>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-2xl border border-[#E8E2D5] shadow-xs flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative w-full md:w-96">
                <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-[#5C7065]" />
                <input type="text" placeholder="Buscar eventos por título o lugar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#E4E0D6] bg-[#FBF9F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4F41]" data-testid="event-search-input" />
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto text-xs font-medium text-[#5C7065]">
                <Calendar className="w-4 h-4 text-[#D4A373]" />
                <span>Ciclo 2026 • Todos los credos unidos</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {events
                .filter(ev => ev.title.toLowerCase().includes(searchQuery.toLowerCase()) || ev.location.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((event) => (
                  <div key={event.id} className="bg-white rounded-2xl overflow-hidden border border-[#E8E2D5] shadow-xs flex flex-col md:flex-row hover:shadow-md transition">
                    <div className="md:w-1/2 relative h-56 md:h-auto">
                      <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                      <span className="absolute top-4 left-4 bg-[#3A4F41] text-white text-xs font-semibold px-3 py-1 rounded-full">{event.category}</span>
                    </div>
                    <div className="md:w-1/2 p-6 flex flex-col justify-between space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs text-[#5C7065] font-semibold">
                          <Calendar className="w-3.5 h-3.5 text-[#D4A373]" />
                          <span>{event.date} • {event.time}</span>
                        </div>
                        <h3 className="font-serif text-xl font-bold text-[#2C2A29]">{event.title}</h3>
                        <p className="text-sm text-[#5C7065]">{event.description}</p>
                      </div>
                      <div className="pt-4 border-t border-[#F2ECE1] space-y-3">
                        <div className="flex items-center gap-2 text-xs text-[#5C7065]">
                          <MapPin className="w-4 h-4 text-[#D4A373]" />
                          <span className="font-medium">{event.location}</span>
                        </div>
                        <button onClick={() => handleEventRegister(event)}
                          className="w-full bg-[#3A4F41] hover:bg-[#2D3E33] text-white font-medium py-2 rounded-lg text-sm transition"
                          data-testid={`register-event-${event.id}`}>
                          Inscribirme al Evento
                        </button>
                      </div>
                    </div>
                  </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ DOCUMENTOS ============ */}
        {activeTab === "documentos" && (
          <div data-testid="view-documentos" className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
            <div className="text-center max-w-3xl mx-auto space-y-4">
              <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">Biblioteca Institucional</span>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C2A29]">Documentos y Comunicados</h1>
              <p className="text-[#5C7065] text-lg">Declaraciones conjuntas, estatutos, protocolos y guías pedagógicas de la Red Interreligiosa de Zárate.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {documents.map((doc) => (
                <div key={doc.id} className="bg-white p-6 rounded-2xl border border-[#E8E2D5] shadow-xs hover:shadow transition flex flex-col justify-between space-y-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#3A4F41] bg-[#3A4F41]/10 px-3 py-1 rounded-full">{doc.category}</span>
                      <span className="text-xs text-[#5C7065] font-medium">{doc.date}</span>
                    </div>
                    <h3 className="font-serif text-xl font-bold text-[#2C2A29]">{doc.title}</h3>
                    <p className="text-sm text-[#5C7065] leading-relaxed">{doc.description}</p>
                  </div>
                  <div className="pt-4 border-t border-[#F4F1EA] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-[#5C7065]">
                      <FileText className="w-4 h-4 text-[#D4A373]" />
                      <span>{doc.fileType} • {doc.fileSize} • {doc.downloads} descargas</span>
                    </div>
                    <button onClick={() => toast.success(`Descargando "${doc.title}"...`)}
                      className="flex items-center gap-1.5 bg-[#3A4F41] hover:bg-[#2D3E33] text-white text-xs font-bold px-4 py-2 rounded-lg transition"
                      data-testid={`download-doc-${doc.id}`}>
                      <Download className="w-3.5 h-3.5" /><span>Descargar</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ CONTACTO ============ */}
        {activeTab === "contacto" && (
          <div data-testid="view-contacto" className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
            <div className="text-center max-w-3xl mx-auto space-y-4">
              <span className="text-[#5C7065] text-xs font-bold uppercase tracking-widest bg-[#EFECE6] px-3 py-1 rounded">Comuníquese</span>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C2A29]">Contacto y Donaciones</h1>
              <p className="text-[#5C7065] text-lg">¿Necesita asistencia o desea colaborar? La Red Interreligiosa está a disposición de la comunidad.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Form */}
              <div className="bg-white p-8 rounded-2xl border border-[#E8E2D5] shadow-xs space-y-6">
                <h3 className="font-serif text-2xl font-bold text-[#2C2A29]">Envíenos un Mensaje</h3>
                {contactSubmitted ? (
                  <div className="bg-[#3A4F41]/10 border border-[#3A4F41] p-6 rounded-xl text-center space-y-3">
                    <CheckCircle2 className="w-12 h-12 text-[#3A4F41] mx-auto" />
                    <h4 className="font-serif font-bold text-lg text-[#2C2A29]">¡Mensaje Recibido!</h4>
                    <p className="text-sm text-[#5C7065]">Gracias por escribirnos. Nos pondremos en contacto a la brevedad.</p>
                    <button onClick={() => setContactSubmitted(false)} className="text-xs font-bold text-[#3A4F41] underline pt-2">
                      Enviar otro mensaje
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-[#4A4745] uppercase mb-1">Nombre Completo *</label>
                      <input type="text" required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder="Ej. Juan Pérez" className="w-full px-4 py-3 rounded-xl border border-[#E4E0D6] bg-[#FBF9F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4F41]" data-testid="contact-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#4A4745] uppercase mb-1">Correo Electrónico *</label>
                      <input type="email" required value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder="juan@ejemplo.com" className="w-full px-4 py-3 rounded-xl border border-[#E4E0D6] bg-[#FBF9F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4F41]" data-testid="contact-email" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#4A4745] uppercase mb-1">Teléfono</label>
                      <input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder="+54 3487..." className="w-full px-4 py-3 rounded-xl border border-[#E4E0D6] bg-[#FBF9F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4F41]" data-testid="contact-phone" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#4A4745] uppercase mb-1">Motivo de Contacto *</label>
                      <select required value={contactForm.reason} onChange={(e) => setContactForm({ ...contactForm, reason: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#E4E0D6] bg-[#FBF9F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4F41]" data-testid="contact-reason">
                        {contactReasons.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#4A4745] uppercase mb-1">Mensaje *</label>
                      <textarea required rows={4} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        placeholder="Escriba su consulta aquí..." className="w-full px-4 py-3 rounded-xl border border-[#E4E0D6] bg-[#FBF9F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4F41]" data-testid="contact-message" />
                    </div>
                    <button type="submit" disabled={contactSubmitting} className="w-full bg-[#3A4F41] hover:bg-[#2D3E33] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-xl transition shadow flex items-center justify-center gap-2" data-testid="contact-submit-btn">
                      <Send className="w-4 h-4" /><span>{contactSubmitting ? "Enviando..." : "Enviar Mensaje"}</span>
                    </button>
                  </form>
                )}
              </div>

              {/* Info + Línea 145 + Donaciones */}
              <div className="space-y-6">
                <div className="bg-[#3A4F41] text-white p-8 rounded-2xl space-y-6 shadow-md">
                  <h3 className="font-serif text-2xl font-bold">Información de Contacto</h3>
                  <div className="space-y-4 text-sm">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#D4A373] shrink-0 mt-0.5" />
                      <span>{siteInfo.address}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#D4A373] shrink-0" />
                      <span>{siteInfo.phone}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-[#D4A373] shrink-0" />
                      <span>{siteInfo.email}</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-xs text-[#E8E2D5]">Horarios de atención: Lunes a Viernes de 9:00 a 16:00 Hs.</p>
                  </div>
                </div>

                {/* Línea 145 */}
                <div className="bg-[#B14A3F] text-white p-6 rounded-2xl shadow-md space-y-4" data-testid="contacto-linea-145-box">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-[#F7D1CB]">Línea Nacional contra la Trata</p>
                      <p className="font-serif text-3xl font-bold">145</p>
                    </div>
                  </div>
                  <p className="text-sm text-[#F7D1CB]">Gratuita · 24 horas · Confidencial. Si necesitás denunciar o pedir ayuda por trata de personas o explotación.</p>
                  <a href="tel:145" className="w-full bg-white text-[#B14A3F] hover:bg-[#F7D1CB] font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 transition">
                    <PhoneCall className="w-4 h-4" /><span>Llamar al 145</span>
                  </a>
                </div>

                {/* Donaciones */}
                <div className="bg-white p-6 rounded-2xl border border-[#E8E2D5] shadow-xs space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#D4A373]/20 text-[#D4A373] flex items-center justify-center">
                      <Heart className="w-5 h-5" />
                    </div>
                    <h3 className="font-serif text-xl font-bold text-[#2C2A29]">Colabora con la Red</h3>
                  </div>
                  <p className="text-sm text-[#5C7065] leading-relaxed">
                    Tu aporte sostiene los programas solidarios, encuentros interreligiosos y asistencia comunitaria en Zárate.
                  </p>
                  <a
                    href={siteInfo.mercadoPagoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-[#D4A373] hover:bg-[#C29262] text-[#2C2A29] font-bold py-3 rounded-xl transition shadow flex items-center justify-center gap-2"
                    data-testid="contact-donate-cta">
                    <DollarSign className="w-4 h-4" />
                    <span>Ir a Mercado Pago (Donar / Colaborar)</span>
                  </a>
                  <div className="bg-[#EFECE6] p-4 rounded-xl border border-[#E4E0D6] space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#3A4F41]">También podés donar por transferencia</p>
                    <div className="text-xs text-[#4A4745] space-y-1">
                      <p><strong>CBU:</strong> <span className="font-mono bg-white px-2 py-0.5 rounded select-all" data-testid="cbu-value">{siteInfo.cbu}</span></p>
                      <p><strong>ALIAS:</strong> <span className="font-mono bg-white px-2 py-0.5 rounded select-all text-[#3A4F41] font-bold" data-testid="alias-value">{siteInfo.alias}</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal de inscripción a eventos */}
      {registrationEvent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !regSubmitting && setRegistrationEvent(null)}
          data-testid="event-registration-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 space-y-5 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !regSubmitting && setRegistrationEvent(null)}
              aria-label="Cerrar"
              className="absolute top-4 right-4 text-[#5C7065] hover:text-[#2C2A29]"
              data-testid="event-reg-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="space-y-2">
              <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-[#D4A373] bg-[#3A4F41]/10 px-2 py-0.5 rounded">Inscripción a evento</span>
              <h3 className="font-serif text-xl font-bold text-[#2C2A29] leading-tight">{registrationEvent.title}</h3>
              <p className="text-xs text-[#5C7065] flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[#D4A373]" /> {registrationEvent.date} • {registrationEvent.time}</p>
              <p className="text-xs text-[#5C7065] flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#D4A373]" /> {registrationEvent.location}</p>
            </div>
            <form onSubmit={submitEventRegistration} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#4A4745] uppercase mb-1">Nombre Completo *</label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={regForm.name}
                  onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                  placeholder="Ej. María López"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#E4E0D6] bg-[#FBF9F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4F41]"
                  data-testid="event-reg-name"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#4A4745] uppercase mb-1">Correo Electrónico *</label>
                <input
                  type="email"
                  required
                  value={regForm.email}
                  onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                  placeholder="maria@ejemplo.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#E4E0D6] bg-[#FBF9F5] text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4F41]"
                  data-testid="event-reg-email"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !regSubmitting && setRegistrationEvent(null)}
                  disabled={regSubmitting}
                  className="flex-1 bg-[#EFECE6] hover:bg-[#E4E0D6] text-[#4A4745] font-semibold py-2.5 rounded-xl transition disabled:opacity-60"
                  data-testid="event-reg-cancel-btn"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={regSubmitting}
                  className="flex-1 bg-[#3A4F41] hover:bg-[#2D3E33] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-2.5 rounded-xl transition shadow flex items-center justify-center gap-2"
                  data-testid="event-reg-submit-btn"
                >
                  <Send className="w-4 h-4" />
                  <span>{regSubmitting ? "Inscribiendo..." : "Confirmar Inscripción"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-[#2D3E33] text-[#E8E2D5] pt-16 pb-12 mt-20 border-t border-[#3A4F41]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_comunidad-dialogos/artifacts/wg5ay1rq_Logo_Red_Interreligiosaa_oficial-removebg-preview.png" 
                alt="Red Interreligiosa Zárate" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-serif font-bold text-sm text-white">Red Interreligiosa de Protección Integral y Restitución de Derechos</span>
            </div>
            <p className="text-xs text-[#C8C0B0] leading-relaxed">{siteInfo.subtitle}</p>
            <p className="text-xs text-[#D4A373] font-semibold italic">"{siteInfo.lema}"</p>
          </div>

          <div className="space-y-3">
            <h4 className="font-serif font-bold text-white text-sm uppercase tracking-wider">Secciones</h4>
            <ul className="space-y-2 text-xs">
              {navItems.map((it) => (
                <li key={it.id}><button onClick={() => setActiveTab(it.id)} className="hover:text-[#D4A373]">{it.label}</button></li>
              ))}
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="font-serif font-bold text-white text-sm uppercase tracking-wider">Contacto y Ayuda</h4>
            <p className="text-xs text-[#C8C0B0] leading-relaxed">
              {siteInfo.address}<br />
              Tel: {siteInfo.phone}<br />
              Email: {siteInfo.email}
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <a href="tel:145" data-testid="footer-linea-145" className="inline-flex items-center gap-1.5 bg-[#B14A3F] text-white font-bold px-3 py-1.5 rounded text-xs hover:bg-[#8A3A32] transition w-fit">
                <PhoneCall className="w-3.5 h-3.5" /> Línea 145 · Trata
              </a>
              <a href={siteInfo.mercadoPagoLink} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-[#D4A373] text-[#2C2A29] font-bold px-3 py-1.5 rounded text-xs hover:bg-[#C29262] transition w-fit"
                data-testid="footer-mp-btn">
                <Heart className="w-3.5 h-3.5" /> Donar (Mercado Pago)
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center text-xs text-[#A89F91]">
          <p>© {new Date().getFullYear()} Red Interreligiosa de Protección Integral y Restitución de Derechos Zárate. Todos los derechos reservados.</p>
          <p className="mt-2 sm:mt-0">Unidad • Dignidad • Justicia • Acción</p>
        </div>
      </footer>
    </div>
  );
}
